import { useEffect, useRef, useState } from "react";
import { agentApi, ingestionApi } from "../../../api/client";
import { COLORS } from "../../../theme/colors";

// Phase B per agent-detail-viewer-v1.md §10 row 15. Per-agent jobs
// table with 5s polling + 7-stage progress bar. "View all platform jobs"
// pivots to Admin → Pipeline Jobs (B22 renames the legacy view).

// Exported for unit testing — the 7 named ingestion stages, in order.
export const STAGES = [
  "assembling", "parsing", "schema_detect", "storing",
  "enriching", "graph_extract", "finalize",
];

// Pure helper (exported for unit tests): how many stages are "done" for a
// given (stage, status). A completed job fills the whole bar; otherwise the
// current stage's index is the count of stages behind it (0 when unknown).
export function computeStageProgress(stage, status) {
  const idx = stage ? STAGES.indexOf(stage) : -1;
  return status === "completed" ? STAGES.length : (idx >= 0 ? idx : 0);
}

// Pure helper (exported for unit tests): render an ISO timestamp in local
// time, treating a bare (offset-less) string as UTC. Falsy / unparseable → "—".
export function formatJobTime(iso) {
  if (!iso) return "—";
  const isoUtc = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z";
  const d = new Date(isoUtc);
  return isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

const STATUS_COLOR = {
  pending:    COLORS.textDim,
  queued:     COLORS.cyan,
  claimed:    COLORS.cyan,
  running:    COLORS.cyan,
  completed:  COLORS.green,
  failed:     COLORS.pink,
  cleaned_up: COLORS.textDim,
};

const fmt = formatJobTime;

const StageBar = ({ stage, status, attempts, max_attempts }) => {
  const idx = stage ? STAGES.indexOf(stage) : -1;
  const completed = computeStageProgress(stage, status);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 320 }}>
      <div style={{ display: "flex", gap: 2 }}>
        {STAGES.map((s, i) => {
          const done = i < completed;
          const cur  = i === idx && status !== "completed" && status !== "failed";
          const fail = status === "failed" && i === idx;
          const c = fail ? COLORS.pink : (done ? COLORS.green : (cur ? COLORS.cyan : COLORS.border));
          return (
            <div key={s} title={s} style={{
              flex: 1, height: 6, borderRadius: 3,
              background: c,
              opacity: done || cur || fail ? 1 : 0.35,
              boxShadow: cur ? `0 0 6px ${c}88` : "none",
              transition: "background 0.2s",
            }} />
          );
        })}
      </div>
      <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
        stage: {stage || "—"} · attempt {attempts ?? 0}/{max_attempts ?? 3}
      </div>
    </div>
  );
};

const Banner = ({ kind, children, onDismiss }) => {
  const color = kind === "error" ? COLORS.pink : COLORS.green;
  return (
    <div style={{
      padding: "8px 12px", borderRadius: 8,
      border: `1px solid ${color}55`, background: `${color}18`,
      color: COLORS.text, fontSize: 12,
      display: "flex", justifyContent: "space-between", gap: 12,
    }}>
      <span>{children}</span>
      {onDismiss && (
        <button onClick={onDismiss} style={{
          background: "transparent", border: "none", color: COLORS.textDim,
          cursor: "pointer", fontSize: 14,
        }}>{"✕"}</button>
      )}
    </div>
  );
};

export default function KnowledgeJobsPanel({ tree, currentUser, onOpenDrawer }) {
  const agentId = tree?.agent?.id;
  const isDeveloper = ["owner", "administrator", "developer"].includes(currentUser?.role);

  const [statusFilter, setStatusFilter] = useState(new Set());
  const [rows, setRows] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState(null);
  const [info, setInfo] = useState(null);

  const fetchOnce = async (firstPage = true) => {
    if (!agentId) return;
    try {
      const params = {};
      if (statusFilter.size) params.status = Array.from(statusFilter).join(",");
      const res = await agentApi.getKnowledgeJobs(agentId, params);
      const d = res?.data || {};
      if (firstPage) setRows(d.rows || []);
      setNextCursor(d.next_cursor || null);
      setErr(null);
    } catch (e) {
      setErr(e?.message || String(e));
    }
  };

  const tickRef = useRef(0);
  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    const tick = ++tickRef.current;
    const refresh = () => {
      if (!cancelled && tick === tickRef.current) fetchOnce(true);
    };
    refresh();
    const t = setInterval(refresh, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [agentId, statusFilter]);

  const toggleStatus = (s) => {
    setStatusFilter(prev => {
      const n = new Set(prev);
      n.has(s) ? n.delete(s) : n.add(s);
      return n;
    });
  };

  const cancel = async (j) => {
    if (!confirm(`Cancel job "${j.id.slice(0, 8)}…"?\n\nThe worker honors the request at the next stage boundary.`)) return;
    setBusyId(j.id);
    setErr(null);
    try {
      await ingestionApi.cancelJob(j.id);
      setInfo("Cancel requested.");
      await fetchOnce(true);
    } catch (e) {
      setErr(`Cancel failed: ${e?.message || String(e)}`);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {info && <Banner kind="success" onDismiss={() => setInfo(null)}>{info}</Banner>}
      {err && <Banner kind="error" onDismiss={() => setErr(null)}>{err}</Banner>}

      {/* Filter bar */}
      <div style={{
        padding: "8px 12px", borderRadius: 10,
        border: `1px solid ${COLORS.border}`, background: COLORS.surface,
        display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center",
      }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, color: COLORS.textDim }}>status</span>
          {["pending", "queued", "claimed", "running", "completed", "failed"].map(s => {
            const on = statusFilter.has(s);
            const c = STATUS_COLOR[s];
            return (
              <button key={s} onClick={() => toggleStatus(s)} style={{
                padding: "3px 9px", borderRadius: 5, fontSize: 10, fontWeight: 600,
                border: `1px solid ${on ? c : COLORS.border}`,
                background: on ? `${c}22` : "transparent",
                color: on ? c : COLORS.textMuted, cursor: "pointer",
              }}>{s}</button>
            );
          })}
          {statusFilter.size > 0 && (
            <button onClick={() => setStatusFilter(new Set())} style={{
              fontSize: 10, color: COLORS.textDim, background: "transparent",
              border: "none", cursor: "pointer",
            }}>clear</button>
          )}
        </div>
        <a href="/admin?tab=ingestion" style={{
          marginLeft: "auto", fontSize: 11, color: COLORS.cyan,
          textDecoration: "none", border: `1px solid ${COLORS.cyan}55`,
          padding: "5px 12px", borderRadius: 6,
        }}>View all platform jobs →</a>
        <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
          polling 5s
        </span>
      </div>

      {rows.length === 0 ? (
        <div style={{ color: COLORS.textDim, fontSize: 12, padding: 18 }}>
          No ingestion jobs for this agent.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {rows.map(j => {
            const c = STATUS_COLOR[j.status] || COLORS.textDim;
            const inFlight = ["pending", "queued", "claimed", "running"].includes(j.status);
            return (
              <div key={j.id}
                onClick={() => onOpenDrawer && onOpenDrawer({ kind: `Job: ${j.id.slice(0, 8)}`, payload: j })}
                style={{
                  padding: "10px 12px", borderRadius: 8,
                  border: `1px solid ${c}33`, background: COLORS.surface,
                  cursor: "pointer",
                  display: "grid", gridTemplateColumns: "120px 90px 1fr 130px 70px",
                  gap: 12, alignItems: "center",
                }}
              >
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.text }}>{j.source_type}</div>
                  <div style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                    {j.id.slice(0, 8)}…
                  </div>
                </div>
                <span style={{
                  fontSize: 10, padding: "3px 9px", borderRadius: 5,
                  background: `${c}22`, color: c, border: `1px solid ${c}55`,
                  textAlign: "center", justifySelf: "start", fontWeight: 600,
                }}>{j.status}</span>
                <StageBar stage={j.stage} status={j.status} attempts={j.attempts} max_attempts={j.max_attempts} />
                <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 10, color: COLORS.textDim }}>
                    {j.processed_documents}/{j.total_documents} docs · {j.total_chunks} chunks
                  </span>
                  <span style={{ fontSize: 10, color: COLORS.textDim, fontFamily: "monospace" }}>
                    {fmt(j.created_at)}
                  </span>
                </div>
                <div onClick={e => e.stopPropagation()}>
                  {inFlight && isDeveloper && (
                    <button onClick={() => cancel(j)} disabled={busyId === j.id} style={{
                      fontSize: 10, padding: "3px 10px", borderRadius: 5,
                      border: `1px solid ${COLORS.pink}55`, background: "transparent",
                      color: COLORS.pink, cursor: busyId === j.id ? "wait" : "pointer",
                    }}>{busyId === j.id ? "…" : "Cancel"}</button>
                  )}
                  {j.status === "failed" && j.error_message && (
                    <span title={j.error_message} style={{
                      fontSize: 10, color: COLORS.pink, cursor: "help",
                    }}>error</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {nextCursor && (
        <div style={{ fontSize: 11, color: COLORS.textDim }}>
          More pages available — narrow the status filter or click a job for detail.
        </div>
      )}
    </div>
  );
}
